import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import AppContext from "../../../AppContext";
import LayoutContext from "../../../LayoutContext";
import noImage from "../../../assets/images/no-image.png";
import { APP_TITLE } from "../../../config/constant";
import { accountStatusColors, getSpanWithColor, getStatusValue } from "../../../utils/CardUtils";
import { formatDefaultDateTime, formatPersonFullName } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import { hasNavigationAccess, hasUserCompletedOnboarding } from "../../../utils/onboardingHelper";
import { roleAccesse } from "../../../utils/roleManager";
import CardComponent from "../../common/cardContainers/CardComponentContainer";
import AccountDetailsCard from "../../common/gridCards/AccountDetailsCard";
import BrandsGridCard from "../../common/gridCards/BrandsGridCard";
import CompanyGridCard from "../../common/gridCards/CompanyGridCard";
import FFCGridCard from "../../common/gridCards/FFCGridCard";
import StoresGridCard from "../../common/gridCards/StoresGridCard";
import UsersGridCard from "../../common/gridCards/UsersGridCard";
import CardGridSkeleton from "../../common/gridCards/animations/CardGridSkeleton";
import CardCounter from "../../common/gridCards/generic/CardCounter";
import ContainerLoader from "../../common/loaders/ContainerLoader";

// #region  SCOPED CSS
const StyledPage = styled.div`
    .dropdownLink:hover {
        background: var(--Buttons-All-Platforms-Gray-300);
        border: 1px solid var(--App-Accent-Outline);
        border-radius: 4px;
    }

    .dropdownLink {
        margin: 0px 8px;
        border: 1px solid transparent;
    }
`;

const StyledCards = styled.div`
    & {
        overflow-y: hidden;
        //height: 100%;
        margin-right: -24px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }

    &.page-no-scroll {
        overflow-y: hidden;
        //height: 100%;
    }
`;

// #endregion

export default function AccountPageDetails() {
    document.title = `${APP_TITLE} - Account Details`;

    // #region STATES
    const navigate = useNavigate();
    const { MainLayoutProps, layoutType } = useOutletContext();

    const { isLoadingAccountPage, setIsLoadingAccountPage } = React.useContext(LayoutContext);
    const { isLoadingApp, userData } = React.useContext(AppContext);
    const navigations = useSelector(state => state.navigationAccesses);

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData,
        navigations: navigations
    };

    const userRole = userData?.role;
    const editAccess = roleAccesse(userRole, "retail brand account admin");

    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const [organizationData, setOrganizationData] = useState([]);
    //const [preLoader, setPreLoader] = useState(false);

    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [loadingStores, setLoadingStores] = useState(false);
    const [loadingFFCs, setLoadingFFCs] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const [companiesData, setCompaniesData] = useState([]);
    const [brandsData, setBrandsData] = useState([]);
    const [storesData, setStoresData] = useState([]);
    const [FFCsData, setFFCsData] = useState([]);
    const [usersData, setUsersData] = useState([]);

    const completedOnboarding = hasUserCompletedOnboarding(navigations);
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        const userID = localStorage.getItem("token");
        const organizationID = localStorage.getItem("currentOrganizationID");
        if (!userID || !viewLayoutStyle || !organizationID) return;

        getOrganizationData(userID);
    }, [localStorage.getItem("currentOrganizationID"), localStorage.getItem("token"), viewLayoutStyle]);

    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);
        setupHeaders(props, completedOnboarding);
    }, [props?.layoutType]);
    // #endregion

    // #region METHODS
    const getOrganizationData = userID => {
        const formData = {
            // id: userID
        };

        Library.makeCommonPostRequest(
            "Organization",
            "getOrganizationData",
            formData,
            false,
            setIsLoadingAccountPage
        ).then(res => {
            if (res.data.status !== 200) return;

            const data = res.data?.data;
            setOrganizationData(data);
            setIsLoadingAccountPage(false);

            requestRemainingData();
        });
    };

    const requestRemainingData = () => {
        const formDataLimit10 = {
            organizationID: localStorage.getItem("currentOrganizationID"),
            companyID: localStorage.getItem("currentCompanyID"),
            limit: 10,
            page: 1
        };

        const formDataLimit12 = {
            organizationID: localStorage.getItem("currentOrganizationID"),
            companyID: localStorage.getItem("currentCompanyID"),
            limit: 12,
            page: 1
        };

        Library.makeCommonPostRequest("Company", "getCompaniesData", formDataLimit10, false, setLoadingCompanies).then(
            res => {
                if (res.data.status !== 200) return;

                const data = res.data?.data?.result;
                setCompaniesData(data);
            }
        );

        Library.makePostRequest("getRetailBrandsData", formDataLimit10, false, setLoadingBrands).then(res => {
            if (res.data.status !== 200) return;

            const data = res.data?.data?.result;
            setBrandsData(data);
        });

        Library.makeCommonPostRequest("Stores", "getStoresData", formDataLimit10, false, setLoadingStores).then(res => {
            if (res.data.status !== 200) return;

            const data = res.data?.data?.result;
            setStoresData(data);
        });

        Library.makePostRequest("getFulfillmentCentersData", formDataLimit12, false, setLoadingFFCs).then(res => {
            if (res.data.status !== 200) return;

            const data = res.data?.data?.result;
            setFFCsData(data);
        });

        Library.makeCommonPostRequest(
            "Users",
            "getUsersData",
            {
                companyID: localStorage.getItem("currentCompanyID"),
                organizationID: localStorage.getItem("currentOrganizationID"),
                limit: 12,
                page: 1
            },
            false,
            setLoadingUsers
        ).then(res => {
            if (res.data.status !== 200) return;

            const data = res.data?.data?.result;
            setUsersData(data);
        });
    };
    // #endregion

    // #region RENDER
    return (
        <StyledPage className={`${props?.className || ""}`}>
            <ContainerLoader
                isLoading={isLoadingAccountPage && !isLoadingApp}
                isLoadingOverlay={false}
                className="w-100"
                //loadingPositionFromTop="300px"
                alignPageCenter={true}>
                <div className="w-100" style={{ marginTop: " 18px" }}>
                    <CardComponent title="Account" spacing="24px">
                        {getAccountDetailsUI(organizationData, navigate, editAccess, completedOnboarding)}
                    </CardComponent>

                    <CardComponent
                        spacing="24px"
                        title="Companies"
                        seeMore={{ enabled: hasNavigationAccess(navigations, "companies"), to: "/companies" }}
                        loading={loadingCompanies}>
                        {getCompaniesUI(companiesData, loadingCompanies, navigate, editAccess, completedOnboarding)}
                    </CardComponent>

                    <CardComponent
                        spacing="24px"
                        title="Retail Brands"
                        seeMore={{ enabled: hasNavigationAccess(navigations, "retailBrands"), to: "/brands" }}
                        loading={loadingBrands}>
                        {getBrandsUI(brandsData, loadingBrands, navigate, editAccess, completedOnboarding)}
                    </CardComponent>

                    <CardComponent
                        spacing="24px"
                        title="Stores"
                        seeMore={{ enabled: hasNavigationAccess(navigations, "stores"), to: "/stores" }}
                        loading={loadingStores}>
                        {getStoresUI(storesData, loadingStores, navigate, editAccess, completedOnboarding)}
                    </CardComponent>

                    <CardComponent
                        spacing="24px"
                        title="FFCenters"
                        seeMore={{
                            enabled: hasNavigationAccess(navigations, "fulfillmentCenters"),
                            to: "/ffcenters/management"
                        }}
                        loading={loadingFFCs}>
                        {getFFCsUI(FFCsData, loadingFFCs)}
                    </CardComponent>

                    <CardComponent
                        spacing="24px"
                        title="Users"
                        seeMore={{ enabled: hasNavigationAccess(navigations, "users"), to: "/users" }}
                        loading={loadingUsers}>
                        {getUsersUI(usersData, loadingUsers, navigate, editAccess, completedOnboarding)}
                    </CardComponent>
                </div>
            </ContainerLoader>
        </StyledPage>
    );
    // #endregion
}

// #region SMALL COMPONENTS
const getAccountDetailsUI = (organizationData, navigate, editAccess, completedOnboarding) => {
    return (
        <div className="flex-row gap16">
            <div className="flex-col col-1 w-100">
                <AccountDetailsCard
                    isCardEnabled={true}
                    title={organizationData?.OrganizationInfo?.name}
                    id={organizationData?.OrganizationInfo?.ourID}
                    image={organizationData?.OrganizationInfo?.mainImageUrl || noImage}
                    profileImage={organizationData?.OrganizationInfo?.squareImageUrl}
                    editAccess={completedOnboarding && editAccess}
                    details={[
                        {
                            key: "Owner:",
                            value: formatPersonFullName(
                                organizationData?.OrganizationOwnerAccount?.BOstagesAccountID?.firstName,
                                organizationData?.OrganizationOwnerAccount?.BOstagesAccountID?.lastName
                            )
                        },
                        {
                            key: "Email:",
                            value: organizationData?.OrganizationOwnerAccount?.BOstagesAccountID?.email || "--"
                        },
                        {
                            key: "Mobile:",
                            value: organizationData?.OrganizationOwnerAccount?.BOstagesAccountID?.phoneNumber || "--"
                        },
                        {
                            key: "Last login:",
                            value: "--"
                        },
                        {
                            key: "Admin:",
                            value: formatPersonFullName(
                                organizationData?.OrganizationAdminAccount?.BOstagesAccountID?.firstName,
                                organizationData?.OrganizationAdminAccount?.BOstagesAccountID?.lastName
                            )
                        },
                        {
                            key: "Email:",
                            value: organizationData?.OrganizationAdminAccount?.BOstagesAccountID?.email || "--"
                        },
                        {
                            key: "Mobile:",
                            value: organizationData?.OrganizationAdminAccount?.BOstagesAccountID?.phoneNumber || "--"
                        },
                        {
                            key: "Last login:",
                            value: "--"
                        },

                        {
                            key: "2FA:",
                            value: organizationData?.OrganizationOwnerAccount?.BOstagesAccountID?.twoFA || (
                                <span
                                    className="text-gray-300"
                                    style={{ position: "relative", right: "1px", fontStyle: "italic" }}>
                                    Not available
                                </span>
                            )
                        },
                        {
                            key: "Timezone:",
                            value: organizationData?.OrganizationInfo?.timeZoneValue || (
                                <span
                                    className="text-gray-300"
                                    style={{ position: "relative", right: "1px", fontStyle: "italic" }}>
                                    Not available
                                </span>
                            )
                        },
                        {
                            key: "Country:",
                            value: organizationData?.OrganizationInfo?.countryValue || "--"
                        },
                        {
                            key: "Status:",
                            value: getSpanWithColor(
                                localStorage.getItem("status") || "--",
                                accountStatusColors,
                                "text-orange-500"
                            )
                        }
                    ]}
                />
            </div>

            <div className="flex-col col-1 w-100">
                <div className="flex-container gap16">
                    <div className="flex-row h-100">
                        <div className="flex-col col-1 w-100">
                            <CardCounter
                                title="Users"
                                addButton={{
                                    visible: true,
                                    disabled: true,
                                    onClick: completedOnboarding
                                        ? () => {
                                              navigate("/account/users");
                                          }
                                        : undefined
                                }}
                                //total={organizationData?.UsersList?.length || "0"}
                                total={organizationData?.Counters?.Users || "--"}
                            />
                        </div>
                        <div className="flex-col col-1 w-100">
                            <CardCounter
                                title="Companies"
                                addButton={{
                                    visible: true,
                                    disabled: true,
                                    onClick: completedOnboarding
                                        ? () => {
                                              navigate("/companies/create");
                                          }
                                        : undefined
                                }}
                                //total={organizationData?.StoresList?.length || "0"}
                                total={localStorage.getItem("activeCompanies") || "--"}
                            />
                        </div>
                        <div className="flex-col col-1 w-100">
                            <CardCounter
                                title="Retail Brands"
                                addButton={{
                                    visible: true
                                    //onClick: completedOnboarding ? () => navigate("/brands/create") : undefined
                                }}
                                //total={organizationData?.BrandsList?.length || "0"}
                                total={localStorage.getItem("activeRetailBrands") || "--"}
                            />
                        </div>
                    </div>

                    {/*<div className="flex-row h-50">
                        <div className="flex-col col-1 w-100">
                            <CardCounter
                                title="Retailers"
                                addButton={{
                                    visible: true
                                    //TODO: onClick: hasNavigationAccess(navigations, "stores") ? () => navigate("/retailers/create") : null
                                }}
                                //total={organizationData?.RetailBrandsList?.length || "0"}
                                total={localStorage.getItem("activeRetailBrands") || "--"}
                            />
                        </div>
                        <div className="flex-col col-1 w-100">
                            <CardCounter
                                title="FFCenters"
                                //total={organizationData?.FFCList?.length || "0"}
                                total={localStorage.getItem("activeFFCenters") || "--"}
                            />
                        </div>
                        <div className="flex-col col-1 w-100">
                            <CardCounter
                                title="Users"
                                addButton={{ visible: true }}
                                //total={organizationData?.UsersList?.length || "0"}
                                total={organizationData?.Counters?.Users || "--"}
                            />
                        </div>
                    </div>
                    */}
                </div>
            </div>
        </div>
    );
};

const getCompaniesUI = (data, isLoading, navigate, editAccess, completedOnboarding) => {
    try {
        const layoutData = data || [];
        const length = layoutData.length;
        const placeholderCount = 10 - length;
        const placeHolderArray = Array(placeholderCount).fill("");

        const minColumnWidth = "370px";
        const gridLayoutStyle = {
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`,
            gap: "24px",
            height: "424px"
        };

        return (
            <StyledCards className={"page-no-scroll"}>
                {isLoading ? (
                    <div style={gridLayoutStyle}>
                        {Array.from({ length: 10 }, () => (
                            <CardGridSkeleton key={uuidv4()} className="borders" height="417.28px" hasBorders={true} />
                        ))}
                    </div>
                ) : (
                    <div className="" style={gridLayoutStyle}>
                        {layoutData?.map(item => {
                            return (
                                <div className="flex-col flex-1" key={item?.ourID}>
                                    <CompanyGridCard
                                        className="w-100"
                                        hasBorders={true}
                                        image={item?.logoURL}
                                        titlePrefix="COMPANY"
                                        title={item?.name}
                                        id={item?.ourID}
                                        isCardEnabled={getStatusValue(item, "status")?.toLowerCase() !== "suspended"}
                                        isCardSuspended={getStatusValue(item, "status")?.toLowerCase() === "suspended"}
                                        //isCardSuspended={status?.toLowerCase() === "suspended"} //TODO: needed to check suspended status?
                                        data={{
                                            createdBy: item?.createdBy,
                                            createdOn: item?.createdOn,
                                            registrationNumber: item?.registrationNumber,
                                            registrationDate: item?.registrationDate,
                                            taxNr: item?.taxNr,
                                            nrOfBrands: item?.nrOfBrands,
                                            nrOfStores: item?.nrOfStores,
                                            status: item?.status
                                        }}
                                        primaryButton={{
                                            title: "DETAILS"
                                        }}
                                        secondaryButton={{
                                            title: "ADD USERS"
                                        }}
                                        edit={{
                                            onClick:
                                                completedOnboarding && editAccess
                                                    ? () => {
                                                          navigate(`/companies/edit/${item?._id}`);
                                                      }
                                                    : undefined
                                        }}
                                    />
                                </div>
                            );
                        })}

                        {!isLoading &&
                            placeHolderArray?.map((_, index) => {
                                return (
                                    <div className="flex-col flex-1" key={index}>
                                        <CompanyGridCard className="w-100" hasBorders={true} isCardEnabled={false} />
                                    </div>
                                );
                            })}
                    </div>
                )}
            </StyledCards>
        );
    } catch (e) {
        return "";
    }
};

const getBrandsUI = (data, isLoading, navigate, editAccess, completedOnboarding) => {
    try {
        const layoutData = data || [];
        const length = layoutData.length;
        const placeholderCount = 10 - length;
        const placeHolderArray = Array(placeholderCount).fill("");

        const minColumnWidth = "370px";
        const gridLayoutStyle = {
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`,
            gap: "24px",
            height: "424px"
        };

        return (
            <StyledCards className={"page-no-scroll"}>
                {isLoading ? (
                    <div style={gridLayoutStyle}>
                        {Array.from({ length: 10 }, () => (
                            <CardGridSkeleton key={uuidv4()} className="borders" height="417.28px" hasBorders={true} />
                        ))}
                    </div>
                ) : (
                    <div className="" style={gridLayoutStyle}>
                        {layoutData?.map(item => {
                            return (
                                <div className="flex-col flex-1" key={item?.ourID}>
                                    <BrandsGridCard
                                        className="w-100"
                                        hasBorders={true}
                                        image={item?.logoURL}
                                        title={item?.name}
                                        id={item?.ourID}
                                        isCardEnabled={getStatusValue(item, "status")?.toLowerCase() === "active"}
                                        isCardSuspended={getStatusValue(item, "status")?.toLowerCase() === "suspended"}
                                        data={{
                                            createdBy: item?.createdBy,
                                            createdOn: formatDefaultDateTime(item?.createdOn),
                                            companyName: item?.companyName,
                                            utilizingShopify: item?.utilizingShopify,
                                            activeStores: item?.totalStores,
                                            activeFFCenters: item?.totalffcenters,
                                            pos: item?.POSsystem,
                                            status: item?.status
                                        }}
                                        /* edit={props?.onEditAction ? { onClick: () => props?.onEditAction(item) } : null}
                                        suspend={{
                                            isReenable: item?.status?.toLowerCase() === "suspended",
                                            onClick: () => (props?.onSuspendAction ? props?.onSuspendAction(item) : null)
                                        }}
                                        delete={
                                            props?.onDeleteAction ? { onClick: () => props?.onDeleteAction(item) } : null
                                        } */
                                        primaryButton={{ title: "Details" }}
                                        secondaryButton={{ title: "Fulfillment Centers" }}
                                        edit={{
                                            onClick:
                                                completedOnboarding && editAccess
                                                    ? () => {
                                                          navigate(`/brands/edit/${item?._id}`);
                                                      }
                                                    : undefined
                                        }}
                                    />
                                </div>
                            );
                        })}

                        {!isLoading &&
                            placeHolderArray?.map((_, index) => {
                                return (
                                    <div className="flex-col flex-1" key={index}>
                                        <BrandsGridCard className="w-100" hasBorders={true} isCardEnabled={false} />
                                    </div>
                                );
                            })}
                    </div>
                )}
            </StyledCards>
        );
    } catch (e) {
        return "";
    }
};

const getStoresUI = (data, isLoading, navigate, editAccess, completedOnboarding) => {
    try {
        const layoutData = data || [];
        const length = layoutData.length;
        const placeholderCount = 10 - length;
        const placeHolderArray = Array(placeholderCount).fill("");

        const minColumnWidth = "370px";
        const gridLayoutStyle = {
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`,
            gap: "24px",
            height: "424px"
        };

        return (
            <StyledCards className={"page-no-scroll"}>
                {isLoading ? (
                    <div style={gridLayoutStyle}>
                        {Array.from({ length: 10 }, () => (
                            <CardGridSkeleton
                                key={uuidv4()}
                                className="borders"
                                height="417.28px"
                                hasBorders={true}
                                isCardEnabled={false}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="" style={gridLayoutStyle}>
                        {layoutData?.map(item => {
                            return (
                                <div className="flex-col flex-1" key={item?.id}>
                                    <StoresGridCard
                                        className="w-100"
                                        hasBorders={true}
                                        image={item?.logoURL}
                                        title={item?.name}
                                        id={item?.ourID}
                                        isCardEnabled={getStatusValue(item)?.toLowerCase() === "active"}
                                        isCardSuspended={getStatusValue(item)?.toLowerCase() === "suspended"}
                                        data={{
                                            createdBy: item?.createdBy,
                                            createdOn: item?.createdOn,
                                            retailBrand: item?.retailBrandName,
                                            country: item?.country,
                                            city: item?.city,
                                            address: item?.address,
                                            company: item?.companyName,
                                            POSsystem: item?.POSsystem,
                                            POSstatus: item?.POSstatus,
                                            //deliveries: item?.deliveries,
                                            status: item?.status,

                                            //EXTRA PARAMETERS FOR DELIVERIES
                                            pickup: item?.pickup,
                                            courier: item?.courier,
                                            shipping: item?.shipping
                                        }}
                                        edit={{
                                            onClick:
                                                completedOnboarding && editAccess
                                                    ? () => {
                                                          navigate(`/stores/edit/${item?.ourID}`);
                                                      }
                                                    : undefined
                                        }}
                                        /* delete={
                                        props?.onDeleteAction ? { onClick: () => props?.onDeleteAction(item) } : null
                                    }
                                    suspend={{
                                        isReenable: item?.status?.toLowerCase() === "suspended",
                                        onClick: () => (props?.onSuspendAction ? props?.onSuspendAction(item) : null)
                                    }} */
                                        primaryButton={{ title: "Details" }}
                                        secondaryButton={{ title: "Add Staff" }}
                                    />
                                </div>
                            );
                        })}

                        {!isLoading &&
                            placeHolderArray?.map((_, index) => {
                                return (
                                    <div className="flex-col flex-1" key={index}>
                                        <StoresGridCard className="w-100" hasBorders={true} isCardEnabled={false} />
                                    </div>
                                );
                            })}
                    </div>
                )}
            </StyledCards>
        );
    } catch (e) {
        return "";
    }
};

const getFFCsUI = (data, isLoading) => {
    try {
        const layoutData = data || [];
        const length = layoutData.length;
        const placeholderCount = 12 - length;
        const placeHolderArray = Array(placeholderCount).fill("");

        const minColumnWidth = "238px";
        const gridLayoutStyle = {
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`,
            gap: "24px",
            height: "424px"
        };

        return (
            <StyledCards className={"page-no-scroll"}>
                {isLoading ? (
                    <div style={gridLayoutStyle}>
                        {Array.from({ length: 12 }, () => (
                            <CardGridSkeleton key={uuidv4()} className="borders" height="417.28px" hasBorders={true} />
                        ))}
                    </div>
                ) : (
                    <div className="" style={gridLayoutStyle}>
                        {layoutData?.map(item => {
                            return (
                                <div className="flex-col flex-1" key={item?.BOtagID}>
                                    <FFCGridCard
                                        className="w-100"
                                        hasBorders={true}
                                        image={item?.storePictureURL}
                                        imageOverlay={item?.labelBrandPictureURL}
                                        title={item?.storeName}
                                        id={item?.BOtagID}
                                        isCardEnabled={getStatusValue(item, "status")?.toLowerCase() !== "suspended"}
                                        isCardSuspended={item?.status?.toLowerCase() === "suspended"}
                                        data={{
                                            retailBrand: item?.retailBrand,
                                            affiliatedTo: item?.affiliatedTo,
                                            acceptedOn: item?.acceptedOn,
                                            status: item?.status
                                        }}
                                        primaryButton={{ title: "Details" }}
                                        secondaryButton={{ title: "Stock" }}
                                    />
                                </div>
                            );
                        })}

                        {!isLoading &&
                            placeHolderArray?.map((_, index) => {
                                return (
                                    <div className="flex-col flex-1" key={index}>
                                        <FFCGridCard className="w-100" hasBorders={true} isCardEnabled={false} />
                                    </div>
                                );
                            })}
                    </div>
                )}
            </StyledCards>
        );
    } catch (e) {
        return "";
    }
};

const getUsersUI = (data, isLoading, navigate, editAccess, completedOnboarding) => {
    try {
        const layoutData = data || [];
        const length = layoutData.length;
        const placeholderCount = 12 - length;
        const placeHolderArray = Array(placeholderCount).fill("");

        const minColumnWidth = "238px";
        const gridLayoutStyle = {
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`,
            gap: "24px",
            height: "424px"
        };

        return (
            <StyledCards className={"page-no-scroll"}>
                {isLoading ? (
                    <div style={gridLayoutStyle}>
                        {Array.from({ length: 12 }, () => (
                            <CardGridSkeleton key={uuidv4()} className="borders" height="417.28px" hasBorders={true} />
                        ))}
                    </div>
                ) : (
                    <div className="" style={gridLayoutStyle}>
                        {layoutData?.map(item => {
                            return (
                                <div className="flex-col flex-1" key={item?.id}>
                                    <UsersGridCard
                                        className="w-100"
                                        hasBorders={true}
                                        image={item?.profilePhotoUrl}
                                        title={formatPersonFullName(item?.firstName, item?.lastName)}
                                        id={item?.id}
                                        isCardEnabled={getStatusValue(item, "status")?.toLowerCase() !== "suspended"}
                                        isCardSuspended={getStatusValue(item, "status")?.toLowerCase() === "suspended"}
                                        data={{
                                            role: item?.role,
                                            labelBrandName: item?.labelBrandName,
                                            companyName: item?.companyName,
                                            organizationName: item?.organizationName,
                                            enrolledOn: item?.createdOn,
                                            currentStatus: item?.status
                                        }}
                                        edit={{
                                            onClick:
                                                completedOnboarding && editAccess
                                                    ? () => {
                                                          navigate(`/users/edit/${item?._id}`);
                                                      }
                                                    : undefined
                                        }}
                                    />
                                </div>
                            );
                        })}

                        {!isLoading &&
                            placeHolderArray?.map((_, index) => {
                                return (
                                    <div className="flex-col flex-1" key={index}>
                                        <UsersGridCard
                                            className="w-100"
                                            hasBorders={true}
                                            defaultSecondTitle={"Account:"}
                                            isCardEnabled={false}
                                        />
                                    </div>
                                );
                            })}
                    </div>
                )}
            </StyledCards>
        );
    } catch (e) {
        return "";
    }
};
// #endregion

function setupHeaders(props, completedOnboarding) {
    props.setControlBarLeftBtns([]);
    props.setControlBarCenterBtns([{ to: "/account", active: true, disabled: false, title: "Account" }]);
    props.setControlBarRightBtns([{ to: "/account", active: true, title: "Details", icon: "microscope" }]);

    props.setFilterHeaderProperties({
        visible: false,
        gridEnabled: true,
        listEnabled: false,
        onRefreshAction: undefined
    });
}
